import type { DownloadAsset } from "./catalog";

export interface CoverFamilySystem {
  id: string;
  name: string;
  character: string;
  grid: string;
  focalRule: string;
  typeRule: string;
  titleScale: "restrained" | "balanced" | "large" | "oversized";
  fontSuggestions: {
    display: string;
    support: string;
    detail?: string;
  };
  paletteRule: string;
  variants: string[];
}

export interface CoverPalette {
  id: string;
  name: string;
  character: string;
  bestFor: string;
  colors: Array<{ name: string; hex: string }>;
}

export const CUSTOM_COVER_PALETTE_ID = "custom";

export interface InteriorLayoutMaster {
  id: string;
  name: string;
  purpose: string;
  structure: string;
  density: "Airy" | "Balanced" | "Information-rich";
}

export interface EvergreenLaunchPlan {
  id: string;
  name: string;
  purpose: string;
  sequence: string[];
}

export interface APlusStoryboard {
  id: string;
  name: string;
  module: string;
  job: string;
  imageDirection: string;
}

export interface LeadMagnetSystem {
  id: string;
  name: string;
  promise: string;
  structure: string;
}

export interface WelcomeSequence {
  id: string;
  name: string;
  emails: string[];
}

export interface CrossSellTemplate {
  id: string;
  name: string;
  trigger: string;
  purpose: string;
}

export const coverFamilySystems: CoverFamilySystem[] = [
  {
    id: "modern-botanical",
    name: "Soft & Organic",
    character: "Grounded, spacious, and quietly premium",
    grid: "Top-aligned imprint marker, central title field, one lower-third focal form",
    focalRule: "Use one original organic form per title; vary the specimen, not the illustration language",
    typeRule: "Editorial serif title with a restrained humanist sans-serif support line",
    titleScale: "balanced",
    fontSuggestions: { display: "Libre Baskerville Semibold", support: "Source Sans 3" },
    paletteRule: "One forest anchor, one parchment field, one title-specific botanical accent",
    variants: ["Single specimen", "Paired leaves", "Seed study", "Branch crop", "Pressed form", "Seasonal bloom"],
  },
  {
    id: "editorial-minimal",
    name: "Modern & Professional",
    character: "Intelligent, restrained, and contemporary",
    grid: "Strong left grid, fine rules, oversize title line, small imprint signature",
    focalRule: "Let typography carry the concept; use one precise supporting mark only",
    typeRule: "High-contrast serif headline with compact grotesk metadata",
    titleScale: "oversized",
    fontSuggestions: { display: "Fraunces Semibold", support: "Inter" },
    paletteRule: "Warm white and ink with one disciplined signal colour per title",
    variants: ["Vertical rule", "Corner index", "Wide title band", "Number field", "Margin note", "Offset block"],
  },
  {
    id: "heritage-library",
    name: "Classic & Established",
    character: "Collected, scholarly, and enduring",
    grid: "Framed title field with archival hierarchy and a consistent lower imprint panel",
    focalRule: "Use original line ornaments or public-domain source studies with documented rights",
    typeRule: "Old-style serif with small capitals and generous letter spacing",
    titleScale: "balanced",
    fontSuggestions: { display: "EB Garamond Semibold", support: "Source Sans 3" },
    paletteRule: "Oxblood, cream, aged brass, and one muted collection accent",
    variants: ["Bookplate", "Archival frame", "Medallion", "Library label", "Engraved border", "Catalogue card"],
  },
  {
    id: "playful-cut-paper",
    name: "Playful & Bright",
    character: "Tactile, optimistic, and imaginative",
    grid: "Bold modular field with layered paper shapes and a highly legible title zone",
    focalRule: "Build one original geometric scene per title using the same edge and shadow language",
    typeRule: "Friendly display sans serif with simple, short supporting copy",
    titleScale: "large",
    fontSuggestions: { display: "Nunito ExtraBold", support: "Atkinson Hyperlegible" },
    paletteRule: "Cobalt, coral, butter, mint, and one neutral ground with accessible contrast",
    variants: ["Stacked shapes", "Window scene", "Paper character", "Object collage", "Pattern field", "Cut-paper path"],
  },
  {
    id: "soft-geometric",
    name: "Gentle & Organised",
    character: "Orderly, modern, and reassuring",
    grid: "Repeated modular motif with a stable title panel and rounded geometric rhythm",
    focalRule: "Change scale, crop, and sequence while preserving the base shape vocabulary",
    typeRule: "Warm geometric sans serif with a compact serif accent",
    titleScale: "balanced",
    fontSuggestions: { display: "Nunito Sans Bold", support: "Lora" },
    paletteRule: "Sage, sand, charcoal, and one low-saturation title colour",
    variants: ["Concentric form", "Modular path", "Quiet grid", "Soft arch", "Nested blocks", "Rhythmic tiles"],
  },
  {
    id: "field-notes",
    name: "Practical & Grounded",
    character: "Curious, practical, and observant",
    grid: "Annotated focal object with labels, index marks, and notebook-like margins",
    focalRule: "Use an original object study or cleared photograph with consistent annotation rules",
    typeRule: "Utility sans serif with a humanist serif title and monospaced micro-labels",
    titleScale: "large",
    fontSuggestions: { display: "Source Serif 4 Semibold", support: "Source Sans 3", detail: "IBM Plex Mono" },
    paletteRule: "Olive, kraft, graphite, and one natural accent sampled from the subject",
    variants: ["Object plate", "Map note", "Specimen label", "Tool study", "Observation grid", "Route sketch"],
  },
  {
    id: "quiet-luxury",
    name: "Elegant & Refined",
    character: "Refined, understated, and giftable",
    grid: "Small central title field, generous material space, and an exact imprint baseline",
    focalRule: "Use texture and proportion as the primary visual; avoid decorative excess",
    typeRule: "Elegant serif with deliberately sparse supporting type",
    titleScale: "restrained",
    fontSuggestions: { display: "Cormorant Garamond Semibold", support: "Source Sans 3" },
    paletteRule: "Espresso, ivory, muted gold, and one mineral accent per title",
    variants: ["Material field", "Fine border", "Small emblem", "Blind-stamp effect", "Quiet band", "Tonal monogram"],
  },
  {
    id: "bold-type",
    name: "Strong & Direct",
    character: "Direct, energetic, and highly legible",
    grid: "Overscale title, tight modular grid, and one small repeatable collection mark",
    focalRule: "Create distinction through crop, rhythm, and accent placement rather than extra imagery",
    typeRule: "Heavy display sans serif paired with compact neutral support type",
    titleScale: "oversized",
    fontSuggestions: { display: "League Spartan Bold", support: "Inter" },
    paletteRule: "Black, bone, and one saturated title-specific accent",
    variants: ["Stacked title", "Split word", "Edge crop", "Number block", "Diagonal field", "Oversize initial"],
  },
  {
    id: "science-grid",
    name: "Scientific & Precise",
    character: "Evidence-led, exact, and technical without feeling clinical",
    grid: "Clear information grid with a measured title block and one diagram-led focal area",
    focalRule: "Use an original diagram, mapped object, or geometric data pattern with generous labels",
    typeRule: "Highly legible utility sans serif with a precise monospaced detail line",
    titleScale: "large",
    fontSuggestions: { display: "IBM Plex Sans Semibold", support: "IBM Plex Sans", detail: "IBM Plex Mono" },
    paletteRule: "Cool blue, clean white, graphite, and one signal colour chosen for contrast",
    variants: ["Data grid", "Object diagram", "Measured detail", "Coordinate field", "Evidence marks", "Technical map"],
  },
];

export const coverPalettes: CoverPalette[] = [
  {
    id: "soft-expressive",
    name: "Soft & expressive",
    character: "Warm, welcoming, and gently feminine",
    bestFor: "Journals, gifts, wellbeing, relationships, and reflective products",
    colors: [
      { name: "Blush pink", hex: "#D99AA6" },
      { name: "Dusty plum", hex: "#735366" },
      { name: "Warm cream", hex: "#FFF5EC" },
      { name: "Rose clay", hex: "#B96668" },
    ],
  },
  {
    id: "strong-grounded",
    name: "Strong & grounded",
    character: "Confident, practical, and traditionally masculine",
    bestFor: "Trades, business, field guides, fitness, and practical how-to products",
    colors: [
      { name: "Deep navy", hex: "#183248" },
      { name: "Workwear tan", hex: "#C49A63" },
      { name: "Steel grey", hex: "#68737B" },
      { name: "Signal orange", hex: "#E06B32" },
    ],
  },
  {
    id: "scientific-precise",
    name: "Scientific & precise",
    character: "Clear, factual, and evidence-led",
    bestFor: "Science, health records, technical guides, research, and educational products",
    colors: [
      { name: "Laboratory blue", hex: "#2567A5" },
      { name: "Clean white", hex: "#F7FAFC" },
      { name: "Graphite", hex: "#26333D" },
      { name: "Data teal", hex: "#2A9D8F" },
    ],
  },
  {
    id: "natural-calm",
    name: "Natural & calm",
    character: "Earthy, reassuring, and unhurried",
    bestFor: "Home, nature, gardening, mindfulness, and sustainable living",
    colors: [
      { name: "Moss green", hex: "#5C7457" },
      { name: "Oatmeal", hex: "#E8DDC7" },
      { name: "Clay brown", hex: "#9C6C4E" },
      { name: "Soft charcoal", hex: "#343A36" },
    ],
  },
  {
    id: "playful-bright",
    name: "Playful & bright",
    character: "Cheerful, energetic, and child-friendly",
    bestFor: "Children, families, activities, classrooms, and celebrations",
    colors: [
      { name: "Cobalt blue", hex: "#2F5FD0" },
      { name: "Sunny yellow", hex: "#F7CB45" },
      { name: "Coral", hex: "#F06C62" },
      { name: "Mint", hex: "#7BC9AD" },
    ],
  },
  {
    id: "bold-monochrome",
    name: "Bold monochrome",
    character: "Direct, modern, and high-contrast",
    bestFor: "Productivity, typography-led covers, premium workbooks, and broad audiences",
    colors: [
      { name: "Near black", hex: "#161616" },
      { name: "Bone white", hex: "#F2EFE8" },
      { name: "Cool grey", hex: "#A5A7AA" },
      { name: "Electric red", hex: "#D93838" },
    ],
  },
];

export function resolveCoverPalette(
  paletteId: string,
  customPalette: { colors: Array<{ name: string; hex: string }> },
): CoverPalette | undefined {
  if (paletteId === CUSTOM_COVER_PALETTE_ID) {
    return {
      id: CUSTOM_COVER_PALETTE_ID,
      name: "My custom colours",
      character: "Chosen by you for this product",
      bestFor: "A product that needs its own colour combination",
      colors: customPalette.colors,
    };
  }

  return coverPalettes.find((palette) => palette.id === paletteId);
}

const interiorMasterSeeds = [
  ["orientation", "Orientation Page", "Explain the page or section before the reader starts", "Purpose, how to use, flexibility note, first action", "Airy"],
  ["section-opener", "Section Opener", "Create a clear transition into a new part of the book", "Section title, useful promise, short orientation, navigation cue", "Airy"],
  ["guided-reflection", "Guided Reflection", "Sequence a thoughtful response without overwhelming the page", "Opening cue, three progressive prompts, writing area, close", "Balanced"],
  ["daily-entry", "Daily Entry", "Support a repeatable short-form practice", "Date, state, focus, action, evidence, closing note", "Balanced"],
  ["weekly-review", "Weekly Review", "Turn a week of activity into useful learning", "Wins, friction, evidence, lesson, adjustment", "Balanced"],
  ["monthly-map", "Monthly Map", "Create a realistic view of commitments and capacity", "Theme, commitments, key dates, capacity, success signals", "Information-rich"],
  ["tracker", "Evidence Tracker", "Keep supporting evidence connected to the relevant entry", "Date, related entry, evidence type, source or file location, notes", "Information-rich"],
  ["checklist", "Action Checklist", "Make a bounded process easy to follow and verify", "Preparation, ordered actions, exceptions, completion review", "Balanced"],
  ["worksheet", "Decision Worksheet", "Guide a reader from options to a documented choice", "Decision, criteria, options, trade-offs, commitment", "Information-rich"],
  ["matrix", "Comparison Matrix", "Compare several options using visible criteria", "Criteria rows, option columns, evidence notes, decision", "Information-rich"],
  ["canvas", "Planning Canvas", "Hold a complete working model on one spread", "Outcome, inputs, constraints, actions, review", "Information-rich"],
  ["timeline", "Timeline Planner", "Translate a result into sequenced milestones", "Start, milestones, dependencies, dates, next action", "Balanced"],
  ["roadmap", "Roadmap Spread", "Show progression across a longer period", "Phases, outcomes, checkpoints, risks, review dates", "Information-rich"],
  ["audit", "Self-audit", "Review quality using evidence rather than impression", "Criteria, score, evidence, critical fix, next check", "Information-rich"],
  ["inventory", "Inventory Page", "Capture a bounded set of items and their status", "Item, source, status, use, notes", "Information-rich"],
  ["log", "Reference Log", "Keep sources or observations retrievable and actionable", "Source, date, key point, relevance, next use", "Information-rich"],
  ["prompt-page", "Prompted Writing Page", "Create generous writing space around one strong prompt", "Context line, primary prompt, optional probe, writing field", "Airy"],
  ["challenge", "Short Challenge", "Support a progressive multi-day experience", "Daily action, friction plan, check, reflection", "Balanced"],
  ["scorecard", "Scorecard", "Make several dimensions visible at a glance", "Dimension, rating, evidence, priority, response", "Information-rich"],
  ["before-after", "Before-and-after Review", "Compare a starting point with observable change", "Starting state, current evidence, unchanged factors, next step", "Balanced"],
  ["conversation", "Conversation Prep", "Prepare a clear and respectful discussion", "Purpose, facts, questions, boundaries, follow-up", "Balanced"],
  ["resource-page", "Resource Page", "Present references without turning the book into a link dump", "Resource, why it matters, access note, use prompt", "Balanced"],
  ["closing-review", "Closing Review", "Help the reader consolidate the value of the book", "Before and after, strongest evidence, lessons, continuation", "Balanced"],
  ["next-volume", "Next-volume Bridge", "Connect a completed title to another useful catalogue job", "Completion recognition, remaining need, honest fit, next title", "Airy"],
] as const;

export const interiorLayoutMasters: InteriorLayoutMaster[] = interiorMasterSeeds.map(
  ([id, name, purpose, structure, density]) => ({
    id,
    name,
    purpose,
    structure,
    density,
  }),
);

const launchPlanSeeds = [
  ["foundation-launch", "Foundation Launch", "Introduce the catalogue promise before promoting individual titles", ["Clarify the message house", "Prepare truthful listings", "Demonstrate the catalogue system", "Open the launch", "Review evidence"]],
  ["single-title-first", "Single-title First", "Finish and publish one useful product before adding more", ["Finish and test the customer files", "Prepare the product page", "Check rights and current channel requirements", "Publish the first product", "Review the live customer experience"]],
  ["catalogue-reveal", "Catalogue Reveal", "Present several related titles without reducing their independent value", ["Define the shared promise", "Explain each reader job", "Build the comparison story", "Publish the reveal", "Route readers by fit"]],
  ["educational-launch", "Educational Launch", "Teach the underlying reader problem through useful content", ["Map five teaching points", "Create examples", "Connect to the book mechanism", "Invite a fit decision", "Review questions"]],
  ["proof-led-launch", "Proof-led Quality Launch", "Lead with process, specificity, and production quality rather than claims", ["Document the process", "Show interior substance", "Explain design decisions", "Publish proof assets", "Collect grounded feedback"]],
  ["author-central", "Author Page Launch", "Coordinate the catalogue with a complete Author Central presence", ["Claim titles", "Write the profile", "Prepare recommendations", "Connect catalogue themes", "Check marketplace availability"]],
  ["a-plus-rollout", "A+ Content Rollout", "Create a consistent From the Publisher story across the catalogue", ["Choose five modules", "Build the comparison table", "Write image briefs", "Apply ASINs", "Review desktop and mobile"]],
  ["sponsored-products", "Sponsored Products Starter", "Prepare a controlled first advertising test without assuming performance", ["Confirm eligibility", "Prepare the detail page", "Define the objective", "Document budget boundaries", "Review search-term evidence"]],
  ["reader-magnet", "Reader Magnet Launch", "Connect the book to a useful companion resource and welcome path", ["Choose the magnet", "Write the insert", "Prepare the opt-in page", "Build the welcome sequence", "Measure consented engagement"]],
  ["catalogue-cross-sell", "Catalogue Cross-sell", "Help readers identify the next useful title by need and fit", ["Map reader jobs", "Create honest bridges", "Build comparison copy", "Place touchpoints", "Review relevance"]],
  ["evergreen-relaunch", "Evergreen Relaunch", "Improve an existing title using current evidence rather than artificial urgency", ["Audit the listing", "Review reader questions", "Refresh supporting assets", "Relaunch educational content", "Record learning"]],
  ["portfolio-review", "Portfolio Review", "Use a launch cycle to improve the whole catalogue", ["Gather listing evidence", "Compare title roles", "Review conversion assumptions", "Prioritize revisions", "Schedule the next test"]],
] as const;

export const evergreenLaunchPlans: EvergreenLaunchPlan[] = launchPlanSeeds.map(
  ([id, name, purpose, sequence]) => ({ id, name, purpose, sequence: [...sequence] }),
);

export const aPlusStoryboards: APlusStoryboard[] = [
  { id: "catalogue-comparison", name: "Catalogue Comparison", module: "Comparison chart", job: "Help readers choose the title that matches their current need", imageDirection: "Consistent cover thumbnails, short fit statement, truthful distinction" },
  { id: "reader-path", name: "Reader Path", module: "Standard image header with text", job: "Explain how the book is used from start to finish", imageDirection: "Simple original process graphic with accessible labels" },
  { id: "inside-the-book", name: "Inside the Book", module: "Three images and text", job: "Show representative interior substance and page variety", imageDirection: "Three cleared page crops with legible captions and no invented content" },
  { id: "imprint-promise", name: "Imprint Promise", module: "Single image and sidebar", job: "Introduce the publishing perspective and quality standard", imageDirection: "Imprint mark, material detail, and one concise process note" },
  { id: "book-mechanism", name: "How It Helps", module: "Standard single left image", job: "Explain the specific mechanism without outcome guarantees", imageDirection: "Original diagram of the reader action and resulting record" },
  { id: "gift-fit", name: "Gift and Use Fit", module: "Multiple image module", job: "Clarify suitable occasions, contexts, and recipient fit", imageDirection: "Realistic context scenes without false lifestyle promises" },
  { id: "quality-process", name: "Quality Process", module: "Standard technical specifications", job: "Make format, contents, and production decisions concrete", imageDirection: "Clean specification table with verified product facts" },
  { id: "author-background", name: "Publisher Background", module: "Single image and sidebar", job: "Give relevant imprint context without inventing authority", imageDirection: "Cleared author or imprint image with a factual biography" },
  { id: "collection-story", name: "Collection Story", module: "Image and dark text overlay", job: "Show the shared catalogue idea while preserving title independence", imageDirection: "Coordinated original still life with covers readable at thumbnail size" },
  { id: "sample-experience", name: "Sample Experience", module: "Standard text plus image", job: "Let the reader understand the tone and interaction style", imageDirection: "One representative cleared spread with short contextual copy" },
];

export const leadMagnetSystems: LeadMagnetSystem[] = [
  { id: "quick-start-companion", name: "Quick-start Companion", promise: "Help the reader take the first useful action before opening the full book", structure: "Orientation, one exercise, example, next step, relevant title bridge" },
  { id: "assessment", name: "Self-assessment", promise: "Help readers identify which catalogue need deserves attention first", structure: "Criteria, evidence-based questions, interpretation bands, responsible next action" },
  { id: "sample-pack", name: "Sample Page Pack", promise: "Show representative page quality and interaction without giving away the complete book", structure: "Cover, orientation, three varied sample pages, fit note, next step" },
  { id: "reference-guide", name: "Reference Guide", promise: "Provide a concise resource readers will want to keep", structure: "Key terms, checklist, examples, sources, related catalogue titles" },
  { id: "seven-day-starter", name: "Seven-day Starter", promise: "Create a bounded experience that demonstrates the book's approach", structure: "Seven small actions, friction notes, reflection, continuation choice" },
  { id: "catalogue-finder", name: "Catalogue Finder", promise: "Help readers select the most relevant next title", structure: "Reader contexts, decision questions, title matches, exclusions, comparison table" },
];

export const welcomeSequences: WelcomeSequence[] = [
  { id: "useful-first-week", name: "Useful First Week", emails: ["Deliver the promised resource", "Explain how to use it", "Teach one supporting idea", "Answer a common fit question", "Offer the next relevant step"] },
  { id: "catalogue-orientation", name: "Catalogue Orientation", emails: ["Welcome to the imprint", "Name the catalogue promise", "Route readers by need", "Show the quality process", "Recommend one next title"] },
  { id: "practice-builder", name: "Practice Builder", emails: ["Choose a workable rhythm", "Reduce starting friction", "Notice evidence", "Adjust without failure language", "Continue with the best-fit resource"] },
  { id: "gift-buyer", name: "Gift Buyer Follow-up", emails: ["Deliver the companion resource", "Explain recipient fit", "Offer a thoughtful presentation idea", "Suggest adjacent gifts honestly", "Invite a preference update"] },
  { id: "learning-series", name: "Five-part Learning Series", emails: ["Define the reader problem", "Show a practical mechanism", "Give an example", "Correct a common misconception", "Connect to the relevant book"] },
  { id: "post-purchase", name: "Post-purchase Support", emails: ["Set expectations", "Help the reader begin", "Solve a usage question", "Encourage a useful review of progress", "Introduce a genuinely adjacent title"] },
];

const crossSellSeeds = [
  ["completion-bridge", "Completion Bridge", "Reader finishes a title", "Connect completed work to one remaining reader job"],
  ["need-based-route", "Need-based Route", "Reader identifies a new priority", "Recommend by current need rather than catalogue order"],
  ["format-preference", "Format Preference", "Reader prefers a particular interaction style", "Route to the title whose interior best fits that behaviour"],
  ["gift-follow-up", "Gift Follow-up", "Buyer purchased for someone else", "Suggest an adjacent recipient or occasion without pressure"],
  ["seasonal-relevance", "Seasonal Relevance", "A genuine seasonal use case appears", "Connect an evergreen title to a timely context honestly"],
  ["bundle-comparison", "Bundle Comparison", "Reader is considering several titles", "Make differences and independent value easy to compare"],
  ["reader-question", "Reader Question", "A recurring question reveals another need", "Answer first, then offer the relevant title as a deeper resource"],
  ["lead-magnet-path", "Lead-magnet Path", "Subscriber completes the companion resource", "Recommend the book that continues the same mechanism"],
  ["author-page-route", "Author Page Route", "Reader browses the wider catalogue", "Organize titles by reader job and starting point"],
  ["a-plus-comparison", "A+ Comparison Route", "Shopper views catalogue comparison content", "Use concise fit statements to reduce the wrong purchase"],
  ["post-launch-learning", "Post-launch Learning Route", "Evidence changes the catalogue priority", "Update cross-sell order without inventing demand"],
  ["next-edition", "Next-edition Bridge", "Reader needs a deeper or updated resource", "Explain the distinct added substance of the next edition or volume"],
] as const;

export const crossSellTemplates: CrossSellTemplate[] = crossSellSeeds.map(
  ([id, name, trigger, purpose]) => ({ id, name, trigger, purpose }),
);

const resource = (
  id: string,
  name: string,
  description: string,
  type: DownloadAsset["type"],
  requiredOffer: DownloadAsset["requiredOffer"],
  content: string,
): DownloadAsset => ({ id, name, description, type, requiredOffer, content });

export const productionResources: DownloadAsset[] = [
  resource("production-brief", "Production Brief", "A decision-ready handoff for cover and interior production.", "Template", "paper-kingdom-production", "# Production Brief\n\nProject:\nImprint lock:\nCover family:\nTitle-level variants:\nInterior system:\nTrim and bleed:\nConfirmed spine source:\nTypography and licences:\nExport settings:\nOpen decisions:\nApproval owner:"),
  resource("rights-ledger", "Rights & Asset Ledger", "Track the source, licence, status, and restrictions for every production asset.", "Worksheet", "paper-kingdom-production", "# Rights & Asset Ledger\n\nAsset | Source | Licence holder | Permitted use | Restrictions | Evidence location | Status\n\nReview AI-generated text and images separately from AI-assisted work. Record the current disclosure decision before publishing."),
  resource("print-preflight", "Print Preflight Checklist", "A careful final review before marketplace upload.", "Worksheet", "paper-kingdom-production", "# Print Preflight\n\n- [ ] Trim, bleed, margins, and gutter match the confirmed specification\n- [ ] Spine width was taken from the current official calculator\n- [ ] Barcode-safe area is clear\n- [ ] Fonts are embedded and licensed\n- [ ] Images meet the intended resolution and colour requirements\n- [ ] Cover text matches metadata\n- [ ] Interior pages are complete, ordered, and substantively differentiated\n- [ ] A physical proof has been reviewed"),
  resource("proof-log", "Physical Proof Log", "Record print observations, revisions, and final approval.", "Worksheet", "paper-kingdom-production", "# Physical Proof Log\n\nTitle:\nProof version:\nDate received:\nTrim and alignment:\nGutter and writing comfort:\nType size and contrast:\nInk and image quality:\nBarcode area:\nPage sequence:\nRequired changes:\nDecision: pending / changes needed / approved"),
  resource("revision-register", "Production Revision Register", "Keep corrections bounded, attributable, and versioned.", "Worksheet", "paper-kingdom-production", "# Production Revision Register\n\nVersion | Date | File | Requested change | Reason | Owner | Status | Approval\n\nNever overwrite the last approved production file. Record the source specification used for every dimensional change."),
  resource("delivery-dossier", "Production Delivery Dossier", "Package final specifications, licences, checks, and handoff notes.", "Template", "paper-kingdom-production", "# Production Delivery Dossier\n\nProject and revision:\nFinal file inventory:\nProduction specifications:\nRights evidence:\nDisclosure decision:\nPreflight result:\nPhysical proof approval:\nKnown limitations:\nArchive location:\nFinal owner:"),
];

export const launchGrowthResources: DownloadAsset[] = [
  resource("listing-record", "Selling Kit", "Keep the finished listing, promotion copy, image plan and publishing steps together.", "Worksheet", "paper-kingdom-launch-growth", "# Selling Kit\n\nSales channel:\nListing title:\nProduct description:\nIdeal buyer:\nChannel-sized search terms or tags:\nSearch-engine title and description, when used:\nShort pitch:\nThree social posts:\nLaunch email subject and body:\nListing-image, screenshot or promotion-image plan:\nCurrent live-account choices to confirm:\nFinal review notes:"),
  resource("a-plus-brief", "A+ Content Brief", "Plan up to five coherent modules for a book or catalogue.", "Template", "paper-kingdom-launch-growth", "# A+ Content Brief\n\nMarketplace:\nApplied ASINs:\nModule 1–5:\nJob of each module:\nHeadline and body:\nImage dimensions and alt text:\nComparison-table facts:\nRights check:\nDesktop and mobile review:\nSubmission status:"),
  resource("launch-calendar", "Thirty-day Launch Calendar", "Coordinate useful content, listing checks, and learning reviews.", "Campaign", "paper-kingdom-launch-growth", "# Thirty-day Launch Calendar\n\nWeek 1 — prepare product truth, listing, Author Central, and assets\nWeek 2 — teach the reader problem and demonstrate the book mechanism\nWeek 3 — publish the launch sequence and monitor questions\nWeek 4 — review observable evidence, correct weak assets, and document learning\n\nDo not manufacture urgency, testimonials, rankings, or sales claims."),
  resource("ads-starter", "Sponsored Products Starter Brief", "Prepare an accountable first advertising test without performance promises.", "Worksheet", "paper-kingdom-launch-growth", "# Sponsored Products Starter Brief\n\nMarketplace and eligibility:\nDetail-page readiness:\nCampaign objective:\nRelevant targeting hypotheses:\nBudget boundary:\nNegative or excluded intent:\nCreative and claims review:\nObservation period:\nPause or revise conditions:\nEvidence log:"),
  resource("reader-funnel", "Reader Funnel Map", "Connect a useful companion resource to consented follow-up and relevant catalogue paths.", "Template", "paper-kingdom-launch-growth", "# Reader Funnel Map\n\nBook entry point:\nLead magnet:\nReader promise:\nQR destination:\nConsent language:\nWelcome sequence:\nPrimary reader job:\nRelevant next titles:\nSuppression and preference options:\nMeasurement notes:"),
  resource("welcome-sequence", "Welcome Email Sequence", "A five-email framework that leads with usefulness.", "Campaign", "paper-kingdom-launch-growth", "# Five-email Welcome Sequence\n\n1. Deliver the promised resource\n2. Explain how to use it well\n3. Teach one supporting idea\n4. Answer a real fit or usage question\n5. Offer the next relevant step\n\nFor every email record: purpose, subject, proof, CTA, exclusions, and unsubscribe requirement."),
  resource("cross-sell-map", "Catalogue Cross-sell Map", "Recommend the next useful title by reader need rather than pressure.", "Worksheet", "paper-kingdom-launch-growth", "# Catalogue Cross-sell Map\n\nCurrent title | Completed reader job | Remaining need | Relevant next title | Why it fits | Why it may not fit | Placement\n\nEvery bridge must preserve the independent value of both titles."),
  resource("growth-review", "Launch & Growth Review", "Separate observable evidence from assumptions after a campaign.", "Worksheet", "paper-kingdom-launch-growth", "# Launch & Growth Review\n\nWhat was published:\nWhat was observed:\nQuestions received:\nListing or funnel friction:\nUnsupported assumptions to remove:\nAssets to improve:\nTests worth repeating:\nTests to stop:\nNext decision and owner:"),
];
